import React, { FC, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Grid, Paper, Typography } from '@mui/material'
import { DataGridPremium, GridColDef, GridActionsCellItem } from '@mui/x-data-grid-premium'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import { User, UserContractor } from 'src/api'
import { DialogNames } from 'src/enums'
import { isViewOnlyUser } from 'src/helpers'
import { useArchiveUserContractorRequest, useUnarchiveUserContractorRequest, useDeleteUserContractorRequest } from 'src/hooks/api'
import { useAddPopupMessage, useDialog } from 'src/hooks/ui'
import UserContractorColumns from 'src/components/columns/userContractor'
import { ArchiveUserContractorModal, UnarchiveUserContractorModal } from './archiveUserContractorModal'
import { DeleteUserContractorModal } from './deleteUserContractorModal'

const baseColumns = [
  UserContractorColumns.PlainName,
  UserContractorColumns.Email,
  UserContractorColumns.Status,
  UserContractorColumns.Role,
  UserContractorColumns.ContractorName,
  UserContractorColumns.CreatedAt
]

interface UserContractorListProps {
  userId: number
  user: Pick<User, 'id' | 'name' | 'email' | 'phone'>
  userContractors: UserContractor[]
  updateUser: () => void
}

export const UserContractorList: FC<UserContractorListProps> = ({ userId, user, userContractors, updateUser }) => {
  const navigate = useNavigate()
  const addMessage = useAddPopupMessage()
  const [selectedUc, setSelectedUc] = useState<UserContractor | null>(null)

  const { openDialog: openArchiveDialog, closeDialog: closeArchiveDialog } = useDialog(DialogNames.ArchiveUserContractor)
  const { openDialog: openUnarchiveDialog, closeDialog: closeUnarchiveDialog } = useDialog(DialogNames.UnarchiveUserContractor)
  const { openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog(DialogNames.DeleteUserContractor)

  const archiveRequest = useArchiveUserContractorRequest({
    options: {
      onSuccess: () => {
        closeArchiveDialog()
        setSelectedUc(null)
        addMessage({ text: 'User contractor was successfully archived', type: 'success' })
        updateUser()
      },
      onError: ({ message: text }) => {
        addMessage({ text, type: 'error' })
      }
    }
  })

  const unarchiveRequest = useUnarchiveUserContractorRequest({
    options: {
      onSuccess: () => {
        closeUnarchiveDialog()
        setSelectedUc(null)
        addMessage({ text: 'User contractor was successfully unarchived', type: 'success' })
        updateUser()
      },
      onError: ({ message: text }) => {
        addMessage({ text, type: 'error' })
      }
    }
  })

  const deleteRequest = useDeleteUserContractorRequest({
    options: {
      onSuccess: () => {
        closeDeleteDialog()
        setSelectedUc(null)
        addMessage({ text: 'User contractor was successfully deleted', type: 'success' })
        updateUser()
      },
      onError: ({ message: text }) => {
        addMessage({ text, type: 'error' })
      }
    }
  })

  const handleEdit = useCallback(
    (row: UserContractor) => () => {
      navigate(`/users/${userId}/users-contractors/${row.id}`)
    },
    [navigate, userId]
  )

  const handleCreateContractor = useCallback(() => {
    navigate('/contractors/new', {
      state: {
        ownerUser: { name: user.name, phone: user.phone, email: user.email }
      }
    })
  }, [navigate, user])

  const handleArchiveUnarchive = useCallback(
    (row: UserContractor) => () => {
      setSelectedUc(row)
      if (row.isArchived) {
        openUnarchiveDialog()
      } else {
        openArchiveDialog()
      }
    },
    [openArchiveDialog, openUnarchiveDialog]
  )

  const handleDelete = useCallback(
    (row: UserContractor) => () => {
      setSelectedUc(row)
      openDeleteDialog()
    },
    [openDeleteDialog]
  )

  const handleConfirmArchive = () => {
    if (selectedUc) {
      archiveRequest.mutate({ userContractorId: selectedUc.id })
    }
  }

  const handleConfirmUnarchive = () => {
    if (selectedUc) {
      unarchiveRequest.mutate({ userContractorId: selectedUc.id })
    }
  }

  const handleConfirmDelete = () => {
    if (selectedUc) {
      deleteRequest.mutate({ userContractorId: selectedUc.id })
    }
  }

  const isUserViewOnly = useMemo(() => isViewOnlyUser(userContractors), [userContractors])

  const columns: GridColDef<UserContractor>[] = useMemo(
    () => [
      ...baseColumns,
      {
        field: 'actions',
        headerName: '',
        type: 'actions' as const,
        width: 50,
        getActions: ({ row }) => [
          <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={handleEdit(row)} showInMenu />,
          <GridActionsCellItem
            icon={row.isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
            label={row.isArchived ? 'Unarchive' : 'Archive'}
            onClick={handleArchiveUnarchive(row)}
            showInMenu
          />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDelete(row)} showInMenu />,
          ...(isUserViewOnly ? [<GridActionsCellItem icon={<AddBusinessIcon />} label="Create a contractor" onClick={handleCreateContractor} showInMenu />] : [])
        ]
      }
    ],
    [handleEdit, handleArchiveUnarchive, handleDelete, handleCreateContractor, isUserViewOnly]
  )

  return (
    <Grid container direction="column" spacing={1} wrap="nowrap">
      <Grid item>
        <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
          User Contractors
        </Typography>
      </Grid>
      <Grid item minWidth={0}>
        <Paper sx={{ flexGrow: 1, width: '100%' }}>
          <DataGridPremium
            rows={userContractors}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            hideFooter={userContractors.length <= 100}
            sx={{
              width: '100%',
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1,
                fontSize: '13px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.6)',
                overflow: 'visible'
              }
            }}
          />
        </Paper>
      </Grid>
      <ArchiveUserContractorModal
        name={selectedUc?.name || ''}
        onConfirm={handleConfirmArchive}
        onCancel={() => {
          closeArchiveDialog()
          setSelectedUc(null)
        }}
        isLoading={archiveRequest.isLoading}
      />
      <UnarchiveUserContractorModal
        name={selectedUc?.name || ''}
        onConfirm={handleConfirmUnarchive}
        onCancel={() => {
          closeUnarchiveDialog()
          setSelectedUc(null)
        }}
        isLoading={unarchiveRequest.isLoading}
      />
      <DeleteUserContractorModal
        name={selectedUc?.name || ''}
        onDelete={handleConfirmDelete}
        onCancel={() => {
          closeDeleteDialog()
          setSelectedUc(null)
        }}
        isLoading={deleteRequest.isLoading}
      />
    </Grid>
  )
}
